import React, { useEffect, useMemo, useState } from 'react';

import { Button } from '@/design-system/button';
import { SearchSelect } from '@/design-system/search-select';
import { useSearchableOptions } from '@/hooks/useSearchableOptions';
import { searchProviderOptions } from '@/services/api/providers';

import { warehouseApi } from '../api/warehouse.api';
import { useActivateOnFocus } from '../hooks/useActivateOnFocus';
import {
  type ClientCatalog,
  type SkuOption,
  skuLabel,
  useSkuSearch,
} from '../hooks/useCatalog';
import type {
  CityOption,
  CityRef,
  ClientRef,
  VendorRef,
  WarehouseSku,
} from '../types';
import { useWarehouse } from '../WarehouseContext';
import { type PickerA11yProps, usePickerA11y } from './pickerA11y';

/* ------------------------------ hub picker ------------------------------- */

interface HubSelectProps extends PickerA11yProps {
  value: number | null;
  onChange: (id: number | null) => void;
  /** Offer "Semua hub" (lists only). */
  allowAll?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
  placeholder?: string;
  /** Hub ids left out of the options (e.g. the source hub of a transfer). */
  exclude?: (number | null)[];
}

export const HubSelect: React.FC<HubSelectProps> = ({
  value,
  onChange,
  allowAll,
  disabled,
  invalid,
  className,
  placeholder = 'Pilih hub',
  exclude,
  'aria-label': ariaLabel,
}) => {
  const { hubs, hubsLoading } = useWarehouse();
  const ref = usePickerA11y(ariaLabel, invalid);
  const excludeKey = (exclude ?? []).join(',');
  const options = useMemo(
    () => [
      ...(allowAll ? [{ value: 'all', label: 'Semua hub' }] : []),
      ...hubs
        .filter((h) => !excludeKey.split(',').includes(String(h.id)))
        .map((h) => ({ value: String(h.id), label: h.name })),
    ],
    [allowAll, hubs, excludeKey]
  );
  const current = value === null ? (allowAll ? 'all' : '') : String(value);
  return (
    <div ref={ref} className={className}>
      <SearchSelect
        options={options}
        value={current}
        selectedLabelFallback={
          value !== null
            ? (hubs.find((h) => h.id === value)?.name ?? `Hub #${value}`)
            : undefined
        }
        onChange={(v) => {
          if (!v) return;
          onChange(v === 'all' ? null : Number(v));
        }}
        placeholder={placeholder}
        loading={hubsLoading}
        allowClear={false}
        disabled={disabled}
        error={invalid}
        emptyMessage="Hub tidak ditemukan"
      />
    </div>
  );
};

/** Page-header hub switcher + Refresh (every WMS list page). */
export const HeaderActions: React.FC<{
  children?: React.ReactNode;
  allowAll?: boolean;
}> = ({ children, allowAll = true }) => {
  const { hubID, setHubID, refresh } = useWarehouse();
  return (
    <>
      <HubSelect
        value={hubID}
        onChange={setHubID}
        allowAll={allowAll}
        className="w-48"
        aria-label="Hub"
      />
      <Button
        tone="neutral"
        style="stroke"
        onClick={refresh}
        data-testid="wms-refresh"
        leftIcon={<i className="ri-refresh-line text-[14px]" />}
      >
        Refresh
      </Button>
      {children}
    </>
  );
};

/* ----------------------------- client picker ----------------------------- */

interface ClientSelectProps extends PickerA11yProps {
  value: ClientRef | null;
  onChange: (client: ClientRef | null) => void;
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
  placeholder?: string;
  allowClear?: boolean;
}

/** Server-searched client (provider) picker. */
export const ClientSelect: React.FC<ClientSelectProps> = ({
  value,
  onChange,
  disabled,
  invalid,
  className,
  placeholder = 'Pilih client',
  allowClear = false,
  'aria-label': ariaLabel,
}) => {
  const ref = usePickerA11y(ariaLabel, invalid);
  const { active, onFocus } = useActivateOnFocus();
  const { options, loading, error, setSearch, labelFor } = useSearchableOptions(
    active && !disabled,
    searchProviderOptions
  );
  const selectOptions = useMemo(
    () => options.map((o) => ({ value: o.id, label: o.name })),
    [options]
  );
  return (
    <div ref={ref} className={className} onFocus={onFocus}>
      <SearchSelect
        options={selectOptions}
        value={value ? String(value.id) : ''}
        selectedLabelFallback={value?.name}
        onChange={(v) => {
          if (!v) {
            onChange(null);
            return;
          }
          const picked = options.find((o) => o.id === v);
          const name = picked?.name || labelFor(v) || `Client #${v}`;
          onChange({
            id: Number(v),
            name,
            // Carried with the name: create calls send it as `clientCode`.
            ...(picked?.code ? { code: picked.code } : {}),
          });
        }}
        onSearchChange={setSearch}
        disableLocalFilter
        loading={loading}
        placeholder={placeholder}
        allowClear={allowClear}
        disabled={disabled}
        error={invalid}
        emptyMessage={error ?? 'Client tidak ditemukan'}
      />
    </div>
  );
};

/* ------------------------------ city picker ------------------------------ */

interface CitySelectProps extends PickerA11yProps {
  value: CityRef | null;
  /** `raw` = the chosen option value (a city id, or an extra option's value). */
  onChange: (city: CityRef | null, raw: string | null) => void;
  /** Shown as selected when the value is one of `extraOptions`. */
  rawValue?: string;
  /** Options before the cities (e.g. the list filter's "Belum dikenali"). */
  extraOptions?: { value: string; label: string }[];
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
  placeholder?: string;
  allowClear?: boolean;
}

/** One fetch of core's city master per page load (~500 rows). */
let citiesCache: Promise<CityOption[]> | null = null;
const loadCities = (): Promise<CityOption[]> => {
  if (!citiesCache) {
    citiesCache = warehouseApi.listCities().catch((err) => {
      citiesCache = null;
      throw err;
    });
  }
  return citiesCache;
};

/** Core-service's city master, searched locally (an order's destination
 *  city, the list filter). Loaded on first focus. */
export const CitySelect: React.FC<CitySelectProps> = ({
  value,
  onChange,
  rawValue,
  extraOptions,
  disabled,
  invalid,
  className,
  placeholder = 'Pilih kota',
  allowClear = false,
  'aria-label': ariaLabel,
}) => {
  const ref = usePickerA11y(ariaLabel, invalid);
  const { active, onFocus } = useActivateOnFocus();
  const [cities, setCities] = useState<CityOption[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!active || disabled || cities) return;
    let alive = true;
    setLoading(true);
    loadCities()
      .then((rows) => alive && setCities(rows))
      .catch(() => alive && setError('Master kota tidak bisa dimuat'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [active, disabled, cities]);
  const options = useMemo(
    () => [
      ...(extraOptions ?? []),
      ...(cities ?? []).map((c) => ({ value: String(c.id), label: c.name })),
    ],
    [cities, extraOptions]
  );
  const extraLabel = extraOptions?.find((o) => o.value === rawValue)?.label;
  return (
    <div ref={ref} className={className} onFocus={onFocus}>
      <SearchSelect
        options={options}
        value={rawValue ?? (value ? String(value.id) : '')}
        selectedLabelFallback={value?.name ?? extraLabel}
        onChange={(v) => {
          if (!v) {
            onChange(null, null);
            return;
          }
          const city = cities?.find((c) => String(c.id) === v);
          onChange(city ? { id: city.id, name: city.name } : null, v);
        }}
        loading={loading}
        placeholder={placeholder}
        allowClear={allowClear}
        disabled={disabled}
        error={invalid}
        emptyMessage={error ?? 'Kota tidak ditemukan'}
      />
    </div>
  );
};

/* ----------------------------- vendor picker ----------------------------- */

/** Server search over ACTIVE vendors (name or code). Stable for the hook deps. */
const searchActiveVendors = (search?: string) =>
  warehouseApi
    .listVendors({ search: search || null, status: 'ACTIVE', limit: 20 })
    .then((r) => r.data.map((v) => ({ id: v.id, name: v.name, code: v.code })));

/** Every vendor, inactive ones too — list filters (old POs keep theirs). */
const searchAllVendors = (search?: string) =>
  warehouseApi
    .listVendors({ search: search || null, limit: 20 })
    .then((r) => r.data.map((v) => ({ id: v.id, name: v.name, code: v.code })));

interface VendorSelectProps extends PickerA11yProps {
  value: VendorRef | null;
  onChange: (vendor: VendorRef | null) => void;
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
  placeholder?: string;
  allowClear?: boolean;
  /** Offer inactive vendors too (list filters). */
  includeInactive?: boolean;
}

/**
 * Server-searched (debounced) vendor picker: ACTIVE vendors only (PO type
 * REGULAR), or every vendor with `includeInactive` (list filters).
 */
export const VendorSelect: React.FC<VendorSelectProps> = ({
  value,
  onChange,
  disabled,
  invalid,
  className,
  placeholder = 'Pilih vendor',
  allowClear = false,
  includeInactive = false,
  'aria-label': ariaLabel,
}) => {
  const ref = usePickerA11y(ariaLabel, invalid);
  const { active, onFocus } = useActivateOnFocus();
  const { options, loading, error, setSearch } = useSearchableOptions(
    active && !disabled,
    includeInactive ? searchAllVendors : searchActiveVendors
  );
  const selectOptions = useMemo(
    () => options.map((o) => ({ value: o.id, label: o.name })),
    [options]
  );
  return (
    <div ref={ref} className={className} onFocus={onFocus}>
      <SearchSelect
        options={selectOptions}
        value={value ? value.id : ''}
        selectedLabelFallback={value?.name}
        onChange={(v) => {
          if (!v) {
            onChange(null);
            return;
          }
          const o = options.find((x) => x.id === v);
          onChange(
            o
              ? { id: o.id, code: o.code, name: o.name }
              : value?.id === v
                ? value
                : { id: v, code: '', name: v }
          );
        }}
        onSearchChange={setSearch}
        disableLocalFilter
        loading={loading}
        placeholder={placeholder}
        allowClear={allowClear}
        disabled={disabled}
        error={invalid}
        emptyMessage={
          error ??
          (includeInactive
            ? 'Vendor tidak ditemukan'
            : 'Vendor aktif tidak ditemukan')
        }
      />
    </div>
  );
};

/* ------------------------------- SKU picker ------------------------------ */

const NO_EXTRA: SkuOption[] = [];
const NONE: ReadonlySet<string> = new Set();

interface SkuSelectProps extends PickerA11yProps {
  catalog: Pick<
    ClientCatalog,
    'clientID' | 'firstPage' | 'skuBy' | 'loading' | 'error'
  >;
  /** Chosen SKU code ('' = none). */
  value: string;
  onPick: (sku: WarehouseSku) => void;
  /** Codes already used on another line (left out of the options). */
  exclude?: ReadonlySet<string>;
  /** Options after the results (PO: "+ SKU baru…"); hidden while unknown. */
  extraOptions?: SkuOption[];
  /** An extra option was picked; `query` is what was typed in the search. */
  onExtraOption?: (value: string, query: string) => void;
  /** Offer a clear button (an optional SKU); `onClear` runs when it is used. */
  allowClear?: boolean;
  onClear?: () => void;
  disabled?: boolean;
  invalid?: boolean;
  placeholder?: string;
  'data-testid'?: string;
}

/**
 * Server-searched SKU picker of one client: the catalog's first page before
 * typing, then `GET /skus?search=` (debounced, the stale search aborted).
 * Never loads the whole catalog. A failed search says so instead of "no SKU"
 * and hides the extra options (unknown ≠ empty).
 */
export const SkuSelect: React.FC<SkuSelectProps> = ({
  catalog,
  value,
  onPick,
  exclude = NONE,
  extraOptions = NO_EXTRA,
  onExtraOption,
  allowClear = false,
  onClear,
  disabled,
  invalid,
  placeholder = 'Pilih SKU',
  'aria-label': ariaLabel,
  'data-testid': testId,
}) => {
  const ref = usePickerA11y(ariaLabel, invalid);
  const search = useSkuSearch(catalog.clientID, !disabled);
  const searching = search.searching;
  const list = searching ? search.rows : catalog.firstPage;
  const error = searching ? search.error : catalog.error;
  const loading = searching ? search.loading : catalog.loading;
  const extraValues = useMemo(
    () => new Set(extraOptions.map((o) => o.value)),
    [extraOptions]
  );
  const options = useMemo(
    () => [
      ...list
        .filter((s) => s.sku === value || !exclude.has(s.sku))
        .map((s) => ({ value: s.sku, label: skuLabel(s) })),
      // Unknown ≠ empty: no "SKU baru" while the list failed or is loading.
      ...(error || loading ? [] : extraOptions),
    ],
    [list, value, exclude, error, loading, extraOptions]
  );
  const chosen = value ? catalog.skuBy.get(value) : undefined;
  const setSearch = search.setSearch;

  return (
    <div ref={ref} className="min-w-0" data-testid={testId}>
      <SearchSelect
        options={options}
        value={value}
        selectedLabelFallback={chosen ? skuLabel(chosen) : value || undefined}
        onChange={(v) => {
          if (!v) {
            if (allowClear) {
              onClear?.();
              setSearch('');
            }
            return;
          }
          if (extraValues.has(v)) onExtraOption?.(v, search.input.trim());
          else {
            const s = list.find((x) => x.sku === v);
            if (s) onPick(s);
          }
          // Back to the first page for the next time the list opens.
          setSearch('');
        }}
        onSearchChange={setSearch}
        disableLocalFilter
        loading={loading}
        placeholder={placeholder}
        allowClear={allowClear}
        disabled={disabled}
        error={invalid}
        emptyMessage={
          error
            ? `Gagal memuat SKU: ${error.message}`
            : searching
              ? 'SKU tidak ditemukan'
              : 'Belum ada SKU di katalog client'
        }
      />
    </div>
  );
};
